<template>
  <v-card class="py-4" outlined>
    <v-data-table
      class="elevation-0"
      :headers="headers"
      :items="items"
      :items-per-page.sync="localItemsPerPage"
      :page.sync="localPage"
      item-key="id"
      :single-expand="false"
      :expanded.sync="localExpanded"
      sort-by="timecreate"
      sort-desc
      show-expand
      :footer-props="{ 'items-per-page-options': [10, 20, 30, 50] }"
      :style="{ '--date-font-scale': settingsStore.getFontSize }"
    >

      <!-- Partner name -->
      <template #[`item.partner_name`]='{ item }'>
        <v-chip
          v-if="isBroker && item.partner"
          class="partner-chip"
        >
          {{ item.partner.name || item.partner.id || '-' }}
        </v-chip>
        <span v-else>-</span>
      </template>

      <!-- Alias (bold) -->
      <template #[`item.alias`]="{ item }">
        <span class="text-body-1 font-weight-bold">{{ item.alias }}</span>
      </template>

      <!-- Operation type -->
      <template #[`item.operationType`]="{ item }">
        <span>{{ formatOperationTypeTitle(item.operationType) }}</span>
      </template>

      <!-- Qty chip -->
      <template #[`item.qty`]="{ item }">
        <v-chip
          v-if="item.qty"
          small
          :text-color="item.side === 'BUY' ? 'alive' : 'orangeColor'"
          :color="item.side === 'BUY' ? 'aliveTransp' : 'yellowTransp'"
        >
          <span class="font-weight-bold">{{ formatPrice(item.qty) }}</span>
        </v-chip>
        <span v-else>-</span>
      </template>

      <!-- Volume BRL chip -->
      <template #[`item.volume_brl`]="{ item }">
        <v-chip
          v-if="hasVolume(item)"
          small
          :text-color="item.status === 'DEAL' ? (item.side === 'BUY' ? 'alive' : 'orangeColor') : '#9E9E9E'"
          :color="item.status === 'DEAL' ? (item.side === 'BUY' ? 'aliveTransp' : 'yellowTransp') : '#2C2C2C'"
        >
          <span class="font-weight-bold">{{ formatVolumeBrl(item) }}</span>
        </v-chip>
        <span v-else>-</span>
      </template>

      <!-- Operation created at -->
      <template #[`item.timecreate`]="{ item }">
        <span class="report-date-cell">{{ item.timecreate ? formatTimelineDate(item.timecreate) : '-' }}</span>
      </template>

      <!-- RFQ deal time -->
      <template #[`item.rfq_timecreate`]="{ item }">
        <span class="report-date-cell">{{ item.rfq_timecreate ? formatTimelineDate(item.rfq_timecreate) : '-' }}</span>
      </template>

      <!-- Client Rate -->
      <template #[`item.rfq_px`]="{ item }">
        <span :style="item.status !== 'DEAL' ? 'color: #9E9E9E;' : ''">
          {{ item.rfq_px != null ? Number(item.rfq_px).toFixed(4) : '-' }}
        </span>
      </template>

      <!-- Side -->
      <template #[`item.side`]="{ item }">
        <span
          class="font-weight-bold"
          :class="{
            'alive--text':       item.side === 'BUY',
            'orangeColor--text': item.side === 'SELL',
          }"
        >{{ item.side || '-' }}</span>
      </template>

      <!-- Company name -->
      <template #[`item.company_name`]="{ item }">
        <span>{{ item.company && item.company.name ? item.company.name : '-' }}</span>
      </template>

      <template #[`item.status`]="{ item }">
        {{ formatStatus(item.status) }}
      </template>

      <!-- Row actions -->
      <template #[`item.actions`]="{ item }">
        <div class="d-flex flex-row flex-gap-2">
        <v-btn x-small color="primary" @click="$emit('export-single-pdf', item)">          
          <span>PDF</span>
        </v-btn>
        <v-btn x-small outlined color="primary" @click="$emit('export-single-csv', item)">            <span style="color:white">CSV</span>
          </v-btn>
        </div>
      </template>

      <!-- Expanded row -->
      <template v-slot:expanded-item="{ headers, item }">
        <reports-expanded-row
          :item="item"
          :headers="headers"
          :is-broker="isBroker"
          :is-partner="isPartner"
          :is-pro="isPro"
          side-field="side"
          status-field="status"
          :can-reconcile="false"
        >
          <template #ids="{ item: row }">
            <!-- Operation ID -->
            <div class="d-flex flex-wrap align-center mb-2">
              <copyable-id :id="row.id" type="Operation" />
            </div>
            <!-- RFQ ID -->
            <div class="d-flex flex-wrap align-center mb-2">
              <copyable-id :id="row.rfqId" type="RFQ" />
            </div>

            <!-- Documents DOWNLOAD & UPLOAD -->
            <div class="d-flex flex-wrap align-center mb-2 ">
              <span class="input-title-text font-weight-bold mr-2">{{ $t('reports.expand.documents') }}:</span>
              <div class="d-flex flex-gap-2 align-center">
                <v-btn
                  x-small
                  outlined
                  color="primary"
                  :loading="downloadingDocs[row.id]"
                  @click="downloadDocuments(row)"
                >
                  <span style="color:white">{{ $t('reports.expand.downloadDocuments') }}</span>
                </v-btn>
                  <v-btn
                  x-small
                  outlined
                  :disabled="row.operationFilesCount === 4"
                  color="primary"
                  @click.stop="openEditFilesModal(row)"
                >
                  <span :style="row.operationFilesCount === 4 ? 'color:grey' : 'color:white'">{{ $t('reports.expand.uploadDocuments') }}</span>
                </v-btn>
              </div>
            </div>
          </template>
        </reports-expanded-row>
      </template>

    </v-data-table>
    <edit-operation-files-modal
      v-model="editFilesModal"
      :operation="editFilesOperation"
      @updated="handleFilesUpdated"
    />
  </v-card>
</template>

<script>
import * as utils from '@/utils.js'
import ReportsExpandedRow  from '@/components/reports/ReportsExpandedRow.vue'
import CopyableId          from '@/components/common/CopyableId.vue'
import EditOperationFilesModal from '@/components/modal/EditOperationFilesModal.vue'
import { useSettingsStore, useSnackbarStore, useImportFiles } from '@/store/index.js'
import {
  createReportDateComparator,
  createReportNumberComparator,
  createReportOperationTypeComparator,
  createReportTextComparator,
  formatReportTimelineDate,
  translateStatus,
} from '@/components/reports/reportHelpers.js'

export default {
  name: 'ReportsOperationTable',
  components: { ReportsExpandedRow, CopyableId, EditOperationFilesModal },
  props: {
    items:     { type: Array,   required: true },
    expanded:  { type: Array,   default: () => [] },
    page:      { type: Number,  default: 1 },
    itemsPerPage: { type: Number,  default: 10 },
    isBroker:  { type: Boolean, default: false },
    isPartner: { type: Boolean, default: false },
    isPro:     { type: Boolean, default: false },
    locale:    { type: String,  default: 'en' },
    now:       { type: Number,  default: () => Date.now() },
  },
  emits: ['update:expanded', 'update:page', 'update:items-per-page', 'export-single-pdf', 'export-single-csv', 'documents-updated'],
  data () {
    return {
      settingsStore: useSettingsStore(),
      downloadingDocs: {},
      editFilesModal: false,
      editFilesOperation: null,
      operationLength: null,
    }
  },
  computed: {
    localExpanded: {
      get () { return this.expanded },
      set (val) { this.$emit('update:expanded', val) }
    },
    localPage: {
      get () { return this.page },
      set (val) { this.$emit('update:page', val) }
    },
    localItemsPerPage: {
      get () { return this.itemsPerPage },
      set (val) { this.$emit('update:items-per-page', val) }
    },
    headers () {
      const compareText = createReportTextComparator(this.locale)
      const compareNumber = createReportNumberComparator()
      const compareDate = createReportDateComparator()
      const compareOperationType = createReportOperationTypeComparator(this.$t('broker.operationTypes'), this.locale)

      const base = [
        { text: this.$t('quoteInfo.alias'),               value: 'alias',          sortable: true, sort: compareText },
        { text: this.$t('quoteInfo.clientName'),           value: 'company_name',   sortable: true, sort: compareText },
        { text: this.$t('broker.labels.transactionPurpose'), value: 'operationType', sortable: true, sort: compareOperationType },
        { text: this.$t('management.brokers.headers.timeCreated'), value: 'timecreate', sortable: true, sort: compareDate },
        { text: this.$t('quoteInfo.rfqAssociationDate'),  value: 'rfq_timecreate', sortable: true, sort: compareDate },
        { text: this.$t('quoteInfo.ccy'),                  value: 'ccy',            sortable: true, sort: compareText },
        { text: this.$t('quoteInfo.amountCcy'),            value: 'qty',            sortable: true, sort: compareNumber },
        { text: this.isPro ? this.$t('quoteInfo.rate') : this.$t('quoteInfo.clientRate'),            value: 'rfq_px', sortable: true, sort: compareNumber },
        { text: this.$t('quoteInfo.volumeCcyBrl'),         value: 'volume_brl',     sortable: true, sort: compareNumber },
        { text: this.$t('quoteInfo.side'),                 value: 'side',           sortable: true, sort: compareText },
        { text: this.$t('quoteInfo.status'),               value: 'status',         sortable: true, sort: compareText },
        { text: '',                                         value: 'actions',        sortable: false },
      ]

      if (this.isBroker) {
        base.unshift({ text: this.$t('reports.partnerName'), value: 'partner_name', sortable: false })
      }

      return base
    },
  },
  methods: {
    hasVolume (item) {
      const vol = item?.volume_brl
      if (vol === null || vol === undefined || vol === '') {
        return false
      }
      return Number.isFinite(Number(vol))
    },
    formatTimelineDate (value) {
      return formatReportTimelineDate(value)
    },
    formatPrice (price) {
      return utils.formatPrice(price)
    },
    formatVolumeBrl (item) {
      const volume = Number(item?.volume_brl)
      if (!Number.isFinite(volume)) return '-'
      return `R$ ${utils.formatBRLPretty(volume)}`
    },
    formatOperationTypeTitle (type) {
      const operationTypes = this.$t('broker.operationTypes')
      return String(operationTypes?.[type] || type || '-')
    },
    formatStatus(status) {
      return translateStatus(status)
    },
    async downloadDocuments (row) {
      if (!row?.id) return
      this.$set(this.downloadingDocs, row.id, true)
      try {
        const name = row.company_name || row.alias || row.id
        await useImportFiles().downloadFiles('operation', row.id, name, Boolean(row.partner))
      } catch (error) {
        useSnackbarStore().open(this.$t('reports.expand.downloadDocumentsError'), 'error')
      } finally {
        this.$set(this.downloadingDocs, row.id, false)
      }
    },
    openEditFilesModal (row) {
      if (row.operationFilesCount === 4) return;
      this.editFilesOperation = {
        ...row,
        id: row.id,
        alias: row.alias || row.operation_alias || '',
        operationType: row.operationType || row.operation_type || '',
        companyId: row.companyId || row.company_id || row.company?.id || null,
        amlCheck: row.amlCheck ?? row.aml_check ?? false
      }
      this.editFilesModal = true
    },
    handleFilesUpdated () {
      this.$emit('documents-updated')
    }
  }
}
</script>

<style scoped>
.report-date-cell {
  display: inline-block;
  font-size: calc(clamp(0.6rem, 1.4vw, 0.8rem) * var(--date-font-scale, 1));
  line-height: 1.25;
  white-space: normal;
}
</style>
